import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '@/config/api.config';

export const companyService = {
  /**
   * De quoi peupler les filtres a choix multiple: les gouvernorats et secteurs
   * reellement presents en base, et les bornes des annees de creation. Une
   * liste ecrite en dur proposerait des choix qui ne rendent rien.
   */
  async getFilterOptions() {
    const response = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.COMPANY.FILTER_OPTIONS}`,
    );
    return response.data;
  },

  async getCompanies(params = {}) {
    const res = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.COMPANY.BASE}`,
      { params: params },
    );
    return res.data;
  },

  /**
   * Les entreprises deja connues qui ressemblent a ce qui est en train d'etre
   * saisi. Le matricule fiscal identifie a coup sur, la raison sociale n'est
   * qu'un indice: l'API rend les rapprochements du plus sur au plus lache
   * (`matchType`), a l'enqueteur de trancher.
   */
  async findMatches({ legalName, matriculeFiscale } = {}) {
    const response = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.COMPANY.MATCHES}`,
      { params: { legalName, matriculeFiscale } },
    );
    return response.data;
  },

  /**
   * De quoi demarrer une enquete sur une entreprise deja enquetee: son
   * identite, et les reponses de structure de sa derniere enquete. Jamais un
   * chiffre d'exercice - un effectif ou un chiffre d'affaires repris de
   * l'annee passee serait une donnee inventee.
   */
  async getSurveyPrefill(id) {
    const response = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.COMPANY.SURVEY_PREFILL(id)}`,
    );
    return response.data;
  },

  /**
   * Les differences entre deux enquetes d'une meme entreprise. Le rapprochement
   * est fait par l'API et non ici: comparer ~105 champs suppose de connaitre le
   * referentiel des questions, le type de chacune et la facon de rapprocher les
   * tableaux (sites, certifications, clients) ligne a ligne. L'ecran ne recoit
   * donc que le resultat: les champs compares, ceux qui ont change, et l'ecart.
   *
   * L'API range toujours l'enquete la plus ancienne dans `left`: l'ordre des
   * deux identifiants envoyes n'a pas a etre garanti par l'appelant.
   */
  async compareSurveys(id, a, b) {
    const response = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.COMPANY.SURVEYS_COMPARE(id)}`,
      { params: { a, b } },
    );
    return response.data;
  },

  async getCompany(id) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.COMPANY.BY_ID(id)}`,
      );
      return response.data;
    } catch (error) {
      console.error('getCompany service error:', error);
      throw error;
    }
  },

  async updateCompany(id, data) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}${API_ENDPOINTS.COMPANY.BY_ID(id)}`,
        data,
      );
      return response.data;
    } catch (error) {
      console.error('updateCompany service error:', error);
      throw error;
    }
  },

  async deleteCompany(id) {
    const res = await axios.delete(
      `${API_BASE_URL}${API_ENDPOINTS.COMPANY.BY_ID(id)}`,
    );
    return res.data;
  },
  async exportCompanies(params = {}) {
    const resp = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.COMPANY.EXPORT}`,
      {
        params,
        responseType: 'blob',
      },
    );
    return resp.data;
  },

  /**
   * La fiche d'une seule entreprise en classeur Excel. Le document est
   * fabrique par l'API: le navigateur n'a sous la main que ce que la fiche
   * affiche, alors que le document attendu reprend l'entreprise entiere et sa
   * mise en forme officielle.
   *
   * Contrairement a `exportCompanies`, la reponse complete est rendue et non
   * le seul blob: le nom de fichier voulu est porte par l'en-tete
   * `Content-Disposition`, que l'appelant doit pouvoir lire.
   */
  async exportCompanyFiche(id) {
    const resp = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.COMPANY.EXPORT_ONE(id)}`,
      {
        responseType: 'blob',
      },
    );
    return resp;
  },

  /** La meme fiche en PDF, fabriquee par l'API pour les memes raisons. */
  async exportCompanyFichePdf(id) {
    const resp = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.COMPANY.EXPORT_ONE_PDF(id)}`,
      {
        responseType: 'blob',
      },
    );
    return resp;
  },
};
